import { Component, h, Prop } from '@stencil/core';

@Component({
  tag: 'biel-search-result',
  styleUrl: 'biel-search-result.css',
  shadow: true,
})
export class BielSearchResult {
  @Prop() resultTitle: string;
  @Prop() resultFragment: string;
  @Prop() resultUrl: string;
  @Prop() searchQuery: string;
  @Prop() isSelected: boolean;
  @Prop() hideDescription: boolean = false;

  cleanMarkdown(text: string): string {
    if (!text) return '';

    return (
      text
        // Remove bold/italic (complete and truncated, e.g., **text, *text).
        .replace(/(\*\*|__)(.*?)(\*\*|__)?/g, '$2') // Bold
        // Remove italic formatting: only match when clearly markdown formatted
        .replace(/(?<=\s)\*([^*]+)\*(?=\s|$)/g, '$1') // Asterisk italic (surrounded by spaces)
        .replace(/(?<=\s)_([^_]+)_(?=\s|$)/g, '$1') // Underscore italic (surrounded by spaces)
        .replace(/^\*([^*]+)\*(?=\s|$)/g, '$1') // Asterisk italic at start
        .replace(/^_([^_]+)_(?=\s|$)/g, '$1') // Underscore italic at start
        // Remove inline code (only when properly formatted as `code`)
        .replace(/(?<!\w)`([^`]+)`(?!\w)/g, '$1') // Complete inline code
        .replace(/(?<!\w)`([^`]+)$/g, '$1') // Truncated inline code at end
        // Remove links (including all truncated forms)
        .replace(/\[([^\]]+)\]\([^)]*\)?/g, '$1') // Complete and truncated links with opening paren
        .replace(/\[([^\]]+)\]\s*$/g, '$1') // Truncated links at end (no opening paren)
        .replace(/\[([^\]]+)$/g, '$1') // Truncated links missing closing bracket
        .replace(/^\]\([^)]*\)?/g, '') // Remove orphaned link syntax at start like "](h"
        .replace(/\]\([^)]*\)?/g, '') // Remove orphaned link syntax in middle
        // Remove orphaned closing parentheses (without opening parentheses)
        .replace(/^[^(]*?\)/g, '') // Remove content and closing paren when no opening paren exists at start
        .replace(/(?<!\()[^(]*?\)/g, '') // Remove content and closing paren when no opening paren in middle
        // Remove orphaned closing parentheses followed by " - " pattern
        .replace(/^\)\s*-\s*/g, '') // Remove ") - " at start when no opening paren
        .replace(/(?<!\(.*?)\)\s*-\s*/g, '') // Remove ") - " in middle when no balanced opening paren
        // Remove headers (e.g., ## Header) and incomplete/truncated headers.
        .replace(/^#{1,6}\s+(.*)/gm, '$1')
        .replace(/#{1,6}\s*/g, '') // Remove orphaned header syntax like "## " without content
        // Remove blockquotes (e.g., > text).
        .replace(/^>\s+/gm, '')
        // Remove horizontal rules (e.g., --- or ***).
        .replace(/^-{3,}$/gm, '')
        .replace(/^\*{3,}$/gm, '')
        // Remove code blocks (e.g., ```code block``` and truncated blocks).
        .replace(/```[\s\S]*?```|```[\s\S]*$/g, '') // Backtick blocks
        .replace(/~~~[\s\S]*?~~~|~~~[\s\S]*$/g, '') // Tilde blocks
        // Remove Markdown tables and pipe characters.
        .replace(/^\|.*?\|$/gm, '') // Complete table rows
        .replace(/(\|---+)+\|?/g, '') // Table dividers and truncated rows
        // Remove consecutive backslashes or hyphens.
        .replace(/\\+/g, '') // Remove all backslashes
        .replace(/-{2,}/g, '') // Remove consecutive hyphens
        .replace(/\|\s*\|(\s*\|)+/g, '') // Remove consecutive pipes
        // Remove pilcrow characters
        .replace(/¶/g, '') // Remove pilcrow (paragraph) symbols
        // Remove emojis
        .replace(
          /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/gu,
          ''
        )
        // Clean up any remaining orphaned markdown syntax
        .replace(/^\]\s*/, '') // Remove orphaned ] at start
        .replace(/^\)\s*/, '') // Remove orphaned ) at start
        .replace(/^\}\s*/, '') // Remove orphaned } at start
        .replace(/^\*+\s*/, '') // Remove orphaned asterisks at start
        .replace(/^_+\s*/, '') // Remove orphaned underscores at start
        // Trim excessive whitespace or empty lines.
        .replace(/^\s+|\s+$/g, '') // Trim surrounding whitespace
        .replace(/\n{2,}/g, '\n') // Collapse multiple newlines
        .replace(/\s{2,}/g, ' ') // Collapse multiple spaces
        .trim()
    );
  }

  highlightMatch(text: string) {
    if (!this.searchQuery || !text) return text;

    // Escape special regex characters in the search query
    const escapedQuery = this.searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedQuery})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part) =>
      part.toLowerCase() === this.searchQuery.toLowerCase() ? (
        <mark class="biel-search-result__mark">{part}</mark>
      ) : (
        part
      )
    );
  }

  render() {
    const cleanedTitle = this.cleanMarkdown(this.resultTitle);
    const cleanedFragment = this.cleanMarkdown(this.resultFragment) || '...';

    return (
      <a
        class="biel-search-result"
        href={this.resultUrl}
        onClick={(event) => {
          event.preventDefault();
        }}
      >
        <div class={`biel-search-result-container ${this.isSelected ? 'selected' : ''}`}>
          <span class="biel-search-result__title">{this.highlightMatch(cleanedTitle)}</span>
          {!this.hideDescription && (
            <span class="biel-search-result__description">
              {this.highlightMatch(cleanedFragment)}
            </span>
          )}
        </div>
      </a>
    );
  }
}
